import React, { useMemo, useRef, useState } from 'react';
import { Box, Cylinder, Extrude, Line, Html } from '@react-three/drei';
import * as THREE from 'three';
import { tokens } from '@fluentui/react-components';
import { isNearPanelEdge, HoverEdgeFrame } from '../utils/edgeHover.jsx';

export const OverlayBtn = ({ onClick, onPointerDown, title, children, grab = false, cursor }) => {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerEnter={() => setHov(true)}
        onPointerLeave={() => setHov(false)}
        style={{
          border: 'none', borderRadius: tokens.borderRadiusCircular, cursor: cursor || (grab ? 'ns-resize' : 'pointer'),
          width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto', userSelect: 'none', flexShrink: 0,
          background: '#fff', color: hov ? '#3b82f6' : '#111',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          transition: 'color 0.15s',
        }}
      >
        {children}
      </button>
      {title && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 7px)', left: '50%',
          transform: hov ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(4px)',
          background: '#111', color: '#fff', padding: '2px 7px', borderRadius: 4,
          fontSize: 10, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          opacity: hov ? 1 : 0,
          transition: 'opacity 0.18s, transform 0.18s',
        }}>
          {title}
          <div style={{
            position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
            borderTop: '4px solid #111',
          }} />
        </div>
      )}
    </div>
  );
};

// --- Medžiagos ---
const carcassColor = "#666666"; 

const chromeMaterial = new THREE.MeshStandardMaterial({
  color: "#e0e0e0",
  roughness: 0.2,
  metalness: 0.9
});

// Chrome dalys (kabyklų strypai ir pan.) paprastai naudoja bendrą chromeMaterial,
// bet kai komponentas yra hover'inamas (mėlyna) arba pažymėtas (žalia), turi rodyti
// tą pačią spalvą kaip ir kiti elementai (Drawer, AdjustableShelf ir kt.) — todėl
// tokiu atveju vietoj bendros medžiagos naudojama atskira, dinamiškos spalvos.
const ChromeOrHighlight = ({ highlightColor }) => (
  highlightColor
    ? <meshStandardMaterial color={highlightColor} roughness={0.2} metalness={0.9} />
    : <primitive object={chromeMaterial} attach="material" />
);

// --- T-Shirt Komponentas ---
const TShirt = ({ position, color = "#ffffff", rotation = [0, 0, 0], scale = 1 }) => {
  const shape = useMemo(() => {
    const s = new THREE.Shape();
    const w = 0.22; 
    const h = 0.30; 
    const sleeve = 0.08;
    const sleeveH = 0.08;
    s.moveTo(-w/2, h);
    s.lineTo(-w/2 - sleeve, h - sleeveH + 0.02); 
    s.lineTo(-w/2 - sleeve + 0.02, h - sleeveH - 0.02);
    s.lineTo(-w/2, h - 0.1);
    s.lineTo(-w/2, 0); 
    s.lineTo(w/2, 0);  
    s.lineTo(w/2, h - 0.1);
    s.lineTo(w/2 + sleeve - 0.02, h - sleeveH - 0.02);
    s.lineTo(w/2 + sleeve, h - sleeveH + 0.02); 
    s.lineTo(w/2, h);
    s.lineTo(0.05, h - 0.03); 
    s.lineTo(-0.05, h - 0.03);
    s.lineTo(-w/2, h);
    return s;
  }, []);

  const extrudeSettings = { depth: 0.01, bevelEnabled: true, bevelSize: 0.002, bevelThickness: 0.002 };

  return (
    <group position={position} rotation={rotation}>
      <group scale={scale}>
        <group position={[0, 0.32, 0]}>
           <mesh>
               <torusGeometry args={[0.02, 0.002, 8, 16, Math.PI]} />
               <primitive object={chromeMaterial} attach="material" />
           </mesh>
           <Cylinder args={[0.002, 0.002, 0.06]} position={[0, -0.03, 0]}>
              <primitive object={chromeMaterial} attach="material" />
           </Cylinder>
           <Line points={[[-0.15, -0.06, 0], [0, -0.01, 0], [0.15, -0.06, 0], [-0.15, -0.06, 0]]} color="#333" lineWidth={1.5} />
        </group>
        <group position={[0, -0.02, -0.005]}>
          <Extrude args={[shape, extrudeSettings]}>
             <meshStandardMaterial color={color} side={THREE.DoubleSide} />
          </Extrude>
        </group>
      </group>
    </group>
  );
};

// --- Rodyklė (Adjustable Shelf) ---
const Arrow = ({ position, rotation, color = "#000" }) => {
  const shape = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-0.015, 0);
    s.lineTo(0.015, 0);
    s.lineTo(0, 0.025);
    s.lineTo(-0.015, 0);
    return s;
  }, []);

  return (
    <group position={position} rotation={rotation}>
        <Extrude args={[shape, { depth: 0.005, bevelEnabled: false }]}>
            <meshStandardMaterial color={color} />
        </Extrude>
        <Box args={[0.008, 0.025, 0.005]} position={[0, -0.0125, 0.0025]}>
            <meshStandardMaterial color={color} />
        </Box>
    </group>
  );
};

// --- Rankenėlė ---
const Handle = ({ position }) => (
  <group position={position}>
    <Box args={[0.12, 0.005, 0.015]} position={[0, 0, 0.01]}>
       <primitive object={chromeMaterial} attach="material" />
    </Box>
    <Box args={[0.005, 0.005, 0.015]} position={[-0.05, 0, -0.005]}>
       <primitive object={chromeMaterial} attach="material" />
    </Box>
    <Box args={[0.005, 0.005, 0.015]} position={[0.05, 0, -0.005]}>
       <primitive object={chromeMaterial} attach="material" />
    </Box>
  </group>
);

// 1. Drabužių kabykla (Clothes Rod)
export const ClothesRod = ({ width, height, highlightColor }) => {
  const rodY = height / 2 - 0.1;
  return (
    <group position={[0, rodY, 0]}> {/* Use rodY for vertical positioning */}
      <Cylinder args={[0.012, 0.012, width, 32]} rotation={[0, 0, Math.PI / 2]}><ChromeOrHighlight highlightColor={highlightColor} /></Cylinder>
      <TShirt position={[0, -0.32, 0]} color="#2979ff" /> {/* Pakaba kabo žemiau strypo, drabužis žemiau pakabos */}
    </group>
  );
};

// 2. Dviguba kabykla
export const DoubleClothesRod = ({ width, height, highlightColor }) => {
  const topRodY = height / 2 - 0.1;
  const bottomRodY = 0;

  return (
    <group>
      {/* Viršutinė dalis */}
      <group position={[0, topRodY, 0]}>
         <Cylinder args={[0.012, 0.012, width, 32]} rotation={[0, 0, Math.PI / 2]}><ChromeOrHighlight highlightColor={highlightColor} /></Cylinder>
         <TShirt position={[0, -0.32, 0]} color="#2979ff" />
      </group>
      {/* Apatinė dalis — mažesnis mastelis, kad drabužis netilptų į žemiau esančią sekciją */}
      <group position={[0, bottomRodY, 0]}>
         <Cylinder args={[0.012, 0.012, width, 32]} rotation={[0, 0, Math.PI / 2]}><ChromeOrHighlight highlightColor={highlightColor} /></Cylinder>
         <TShirt position={[0, -0.21, 0]} color="#ff4081" scale={0.65} />
      </group>
    </group>
  );
};

// 3. Vertikalus atskyrimas
export const VerticalDivider = ({ height, depth, color = carcassColor }) => {
  return (
    <Box args={[0.018, height, depth]} position={[0, 0, 0]}>
      <meshStandardMaterial color={color} />
    </Box>
  );
};

// 4. Vidinis stalčius (VIENAS)
export const Drawer = ({ width, height, depth, color = carcassColor, wallThickness = 0.018, hasBackWall = false, hideFrontPanel = false, customHeight, positionOffsetY = 0, onPositionOffsetChange, onHeightChange, isSelected = false }) => {
  const drawerHeight = customHeight != null ? Math.min(customHeight, height) : Math.min(height, 0.20);
  const maxPosOffset = Math.max(0, height - drawerHeight);
  const clampedOffset = Math.max(0, Math.min(positionOffsetY, maxPosOffset));
  const posY = height / 2 - drawerHeight / 2;
  const T = 0.016;
  const backWallZ = -(depth - T) / 2 + (hasBackWall ? 0.05 : -wallThickness / 2);

  const dragRef = useRef(null);
  const [heightEditing, setHeightEditing] = useState(false);
  const [heightLocalVal, setHeightLocalVal] = useState('');

  const startDrag = (e) => {
    if (!onPositionOffsetChange) return;
    dragRef.current = { startY: e.clientY, startOffset: clampedOffset };
    document.body.style.cursor = 'grabbing';
    let rafId = null;
    let latestY = e.clientY;
    const applyFrame = () => {
      rafId = null;
      if (!dragRef.current) return;
      const dy = (dragRef.current.startY - latestY) / 300;
      onPositionOffsetChange(Math.max(0, Math.min(maxPosOffset, dragRef.current.startOffset + dy)));
    };
    const onMove = (me) => {
      if (!dragRef.current) return;
      latestY = me.clientY;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };
    const onUp = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      dragRef.current = null;
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <group position={[0, posY, 0]}>
      <Box args={[width - T * 2, T, depth - T]} position={[0, -drawerHeight / 2 + T / 2, -T / 2]}>
        <meshStandardMaterial color={color} roughness={0.5} />
      </Box>
      <Box args={[T, drawerHeight - T, depth - T]} position={[-width / 2 + T / 2, T / 2, -T / 2]}>
        <meshStandardMaterial color={color} roughness={0.5} />
      </Box>
      <Box args={[T, drawerHeight - T, depth - T]} position={[width / 2 - T / 2, T / 2, -T / 2]}>
        <meshStandardMaterial color={color} roughness={0.5} />
      </Box>
      <Box args={[width - T * 2, drawerHeight - T, T]} position={[0, T / 2, backWallZ]}>
        <meshStandardMaterial color={color} roughness={0.5} />
      </Box>
      {!hideFrontPanel && (
        <Box args={[width - 0.002, drawerHeight - 0.002, T]} position={[0, 0, depth / 2 - T / 2]}>
          <meshStandardMaterial color={color} roughness={0.6} />
        </Box>
      )}
      {isSelected && (
        <Html position={[0, drawerHeight / 2 + 0.04, depth / 2 + 0.01]} center zIndexRange={[200, 0]}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', pointerEvents: 'auto' }}>
            {onPositionOffsetChange && maxPosOffset > 0.001 && (
              <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startDrag(e); }} title="Perkelti (laikyti ir vilkti)">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
              </OverlayBtn>
            )}
            <div
              style={{ background: '#000', padding: '1px 4px', borderRadius: 3, minWidth: 32, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: onHeightChange ? 'pointer' : 'default', userSelect: 'none' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onHeightChange && !heightEditing ? () => { setHeightEditing(true); setHeightLocalVal(String(Math.round(drawerHeight * 100))); } : undefined}
            >
              {heightEditing ? (
                <input
                  autoFocus type="text" value={heightLocalVal}
                  onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setHeightLocalVal(v); }}
                  onFocus={(e) => e.target.select()}
                  onBlur={() => { setHeightEditing(false); const v = parseFloat(heightLocalVal); if (!isNaN(v) && v > 0 && onHeightChange) onHeightChange(v / 100); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setHeightEditing(false); }}
                  style={{ width: 32, border: 'none', outline: 'none', background: '#000', color: '#fff', fontSize: 11, textAlign: 'center', padding: 0 }}
                />
              ) : (
                <span style={{ color: '#fff', fontSize: 11 }}>{Math.round(drawerHeight * 100)}</span>
              )}
            </div>
          </div>
        </Html>
      )}
    </group>
  );
};

// Stalčiaus galinė plokštė — rodoma Vidus žingsnyje kaip reprezentacija
export const DrawerBackPanel = ({ width, height, depth, color = carcassColor, wallThickness = 0.018, hasBackWall = false }) => {
  const drawerHeight = Math.min(height, 0.20);
  const posY = height / 2 - drawerHeight / 2;
  const T = 0.016;
  const backWallZ = -(depth - T) / 2 + (hasBackWall ? 0.05 : -wallThickness / 2);
  return (
    <group position={[0, posY, 0]}>
      <Box args={[width - T * 2, drawerHeight - T, T]} position={[0, T / 2, backWallZ]}>
        <meshStandardMaterial color={color} roughness={0.5} />
      </Box>
    </group>
  );
};

// 5 & 6. Stalčiai šonuose
export const SideDrawerBlock = ({ width, height, depth, side = 'left', color = carcassColor, wallThickness = 0.018, hasBackWall = false }) => {
  const drawerWidth = (width / 2) - 0.02;
  return (
    <group>
      {/* Centrinis vertikalus atskyrimas */}
      <VerticalDivider height={height} depth={depth} color={color} />
      {/* Stalčius kairėje arba dešinėje */}
      <group position={[side === 'left' ? -width/4 -0.01 : width/4 + 0.01, 0, 0]}>
         <Drawer width={drawerWidth} height={height} depth={depth} color={color} wallThickness={wallThickness} hasBackWall={hasBackWall} />
      </group>
    </group>
  );
};

// 7. Dvigubas stalčius
export const DoubleDrawer = ({ width, height, depth, color = carcassColor, wallThickness = 0.018, hasBackWall = false }) => {
  const drawerWidth = (width / 2) - 0.02;
  return (
    <group>
       <group position={[-width/4 - 0.01, 0, 0]}>
         <Drawer width={drawerWidth} height={height} depth={depth} color={color} wallThickness={wallThickness} hasBackWall={hasBackWall} />
       </group>
       <group position={[width/4 + 0.01, 0, 0]}>
         <Drawer width={drawerWidth} height={height} depth={depth} color={color} wallThickness={wallThickness} hasBackWall={hasBackWall} />
       </group>
       {/* Centrinis vertikalus atskyrimas */}
       <VerticalDivider height={height} depth={depth} color={color} />
    </group>
  );
};

// 8. Ištraukiama kabykla — montuojama prie sekcijos viršaus
export const PullOutHanger = ({ width, height, depth, highlightColor }) => {
  const mountY = (height ? height / 2 - 0.05 : 0);
  return (
    <group position={[0, mountY, 0]}>
      <Box args={[0.05, 0.04, depth - 0.05]} position={[0, 0, 0]}>
        <ChromeOrHighlight highlightColor={highlightColor} />
      </Box>
      <Cylinder args={[0.01, 0.01, 0.08, 16]} rotation={[0, 0, Math.PI/2]} position={[0, -0.01, depth/2 - 0.02]}>
         <meshStandardMaterial color={highlightColor || "black"} />
      </Cylinder>
    </group>
  );
};

// 9. Pantografas
export const Pantograph = ({ width, height, highlightColor }) => {
  const bracketW = 0.04;
  const bracketH = 0.22;
  const bracketD = 0.10;
  const armX = width / 2 - bracketW / 2 - 0.01;
  const topY = height / 2 - 0.04;
  const armLen = Math.min(0.80, height * 0.82);
  const bottomY = topY - armLen;
  const bracketCenterY = bottomY + bracketH / 2;

  return (
    <group>
      {/* Horizontal clothes rod at top */}
      <Cylinder args={[0.015, 0.015, width - 0.06, 16]} rotation={[0, 0, Math.PI / 2]} position={[0, topY, 0]}>
        <ChromeOrHighlight highlightColor={highlightColor} />
      </Cylinder>
      {/* Left vertical arm */}
      <Cylinder args={[0.008, 0.008, armLen, 12]} position={[-armX, bottomY + armLen / 2, 0]}>
        <ChromeOrHighlight highlightColor={highlightColor} />
      </Cylinder>
      {/* Right vertical arm */}
      <Cylinder args={[0.008, 0.008, armLen, 12]} position={[armX, bottomY + armLen / 2, 0]}>
        <ChromeOrHighlight highlightColor={highlightColor} />
      </Cylinder>
      {/* Central vertical pull hook — hangs from centre of top bar */}
      <Cylinder args={[0.005, 0.005, 0.60, 8]} position={[0, topY - 0.30, 0]}>
        <ChromeOrHighlight highlightColor={highlightColor} />
      </Cylinder>
      {/* Left mounting bracket */}
      <Box args={[bracketW, bracketH, bracketD]} position={[-armX, bracketCenterY, 0]}>
        <meshStandardMaterial color="#333" />
      </Box>
      {/* Right mounting bracket */}
      <Box args={[bracketW, bracketH, bracketD]} position={[armX, bracketCenterY, 0]}>
        <meshStandardMaterial color="#333" />
      </Box>
    </group>
  );
};

// 10. Kintamo aukščio lentyna
export const AdjustableShelf = ({ width, height, depth, color = carcassColor, positionOffsetY = 0, onPositionOffsetChange, isSelected = false }) => {
  const shelfH = 0.018;
  const maxOffset = Math.max(0, height / 2 - shelfH / 2);
  const clampedPosY = Math.max(-maxOffset, Math.min(maxOffset, positionOffsetY));

  const dragRef = useRef(null);
  const startDrag = (e) => {
    if (!onPositionOffsetChange) return;
    dragRef.current = { startY: e.clientY, startOffset: clampedPosY };
    document.body.style.cursor = 'grabbing';
    let rafId = null;
    let latestY = e.clientY;
    const applyFrame = () => {
      rafId = null;
      if (!dragRef.current) return;
      const dy = (dragRef.current.startY - latestY) / 300;
      onPositionOffsetChange(Math.max(-maxOffset, Math.min(maxOffset, dragRef.current.startOffset + dy)));
    };
    const onMove = (me) => {
      if (!dragRef.current) return;
      latestY = me.clientY;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };
    const onUp = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      dragRef.current = null;
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <group position={[0, clampedPosY, 0]}>
      <Box args={[width - 0.002, shelfH, depth - 0.02]} position={[0, 0, 0]}>
        <meshStandardMaterial color={color} />
      </Box>
      <group position={[0, 0, depth / 2 + 0.01]}>
        <Arrow position={[0, 0.05, 0]} color="#333" />
        <Arrow position={[0, -0.05, 0]} rotation={[0, 0, Math.PI]} color="#333" />
      </group>
      {isSelected && onPositionOffsetChange && maxOffset > 0 && (
        <Html position={[0, shelfH / 2 + 0.04, depth / 2 + 0.01]} center zIndexRange={[150, 0]}>
          <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startDrag(e); }} title="Perkelti (laikyti ir vilkti)">
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
          </OverlayBtn>
        </Html>
      )}
    </group>
  );
};

// 11. Lentyna su bėgeliais
export const ShelfWithRails = ({ width, height, depth, offset = 0, onOffsetChange, color = carcassColor, isSelected: isExternalSelected = false }) => {
  const compHeight = 0.06; // fiksuotas bendras aukštis — 6cm
  const maxOffset = Math.max(0, height - compHeight);
  const clampedOffset = Math.min(Math.max(0, offset), maxOffset);
  // be poslinkio prisiglaudžia prie skyriaus apatinės lentynos
  const posY = -height / 2 + compHeight / 2 + clampedOffset;

  const [selected, setSelected] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Žr. CabinetPanel (SingleCabinet3D.jsx) identišką edgeHoveredRef komentarą — click
  // tikrina PASKUTINĮ onPointerMove rezultatą, ne perskaičiuoja iš naujo, kad hover
  // (žalia/rėmelis) ir click (pažymėjimas) VISADA sutaptų.
  const hoveredRef = useRef(false);
  const dragRef = useRef(null);
  const startMoveDrag = (e) => {
    if (!onOffsetChange) return;
    dragRef.current = { startY: e.clientY, startOffset: clampedOffset };
    document.body.style.cursor = 'grabbing';
    let rafId = null;
    let latestY = e.clientY;
    const applyFrame = () => {
      rafId = null;
      if (!dragRef.current) return;
      const dy = (dragRef.current.startY - latestY) / 300;
      onOffsetChange(Math.min(Math.max(0, dragRef.current.startOffset + dy), maxOffset));
    };
    const onMove = (me) => {
      if (!dragRef.current) return;
      latestY = me.clientY;
      if (rafId == null) rafId = requestAnimationFrame(applyFrame);
    };
    const onUp = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      dragRef.current = null;
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const shelfArgs = [width - 0.04, compHeight, depth - 0.06];
  return (
    <group position={[0, posY, 0]}>
       <Box
         args={shelfArgs}
         position={[0, 0, 0]}
         onPointerMove={onOffsetChange ? (e) => {
           e.stopPropagation();
           const near = isNearPanelEdge(e, shelfArgs[0], shelfArgs[1], undefined, hoveredRef.current);
           hoveredRef.current = near;
           setHovered(near);
           document.body.style.cursor = near ? 'pointer' : '';
         } : undefined}
         onPointerOut={onOffsetChange ? () => { hoveredRef.current = false; setHovered(false); document.body.style.cursor = ''; } : undefined}
         /* stopPropagation PRIEŠ briaunos patikrą — žr. SingleCabinet3D.jsx CabinetPanel
            komentarą apie tą pačią "paspaudimas iškrenta iki FurnitureGroup" klaidą. */
         onClick={onOffsetChange ? (e) => { e.stopPropagation(); if (!hoveredRef.current) return; setSelected(s => !s); } : undefined}
       >
          <meshStandardMaterial color={color} />
          <Box args={[width - 0.04, compHeight, 0.02]} position={[0, 0, depth/2 - 0.03]} >
             <meshStandardMaterial color={color} />
          </Box>
       </Box>
       <HoverEdgeFrame width={shelfArgs[0]} height={shelfArgs[1]} depth={shelfArgs[2]} visible={hovered || selected} />
       <Box args={[0.015, 0.03, depth]} position={[-width/2 + 0.01, 0, 0]}><primitive object={chromeMaterial} attach="material" /></Box>
       <Box args={[0.015, 0.03, depth]} position={[width/2 - 0.01, 0, 0]}><primitive object={chromeMaterial} attach="material" /></Box>
       {(selected || isExternalSelected) && onOffsetChange && maxOffset > 0 && (
         <Html position={[0, compHeight / 2 + 0.04, depth / 2]} center zIndexRange={[150, 0]}>
           <OverlayBtn grab onPointerDown={(e) => { e.stopPropagation(); startMoveDrag(e); }} title="Perkelti (laikyti ir vilkti)">
             <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
           </OverlayBtn>
         </Html>
       )}
    </group>
  );
};
